import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { BarChart, PieChart, TrendingUp, Users, Clock, Shield } from 'lucide-react';

interface RequestStats {
  totalRequests: number;
  activeRequests: number;
  completedRequests: number;
  averageBidsPerRequest: number;
  averageResponseTime: number;
  regionBreakdown: { [key: string]: number };
  ageBreakdown: { [key: string]: number };
  coverageTypeBreakdown: { [key: string]: number };
}

export function RequestStats() {
  const [stats, setStats] = useState<RequestStats>({
    totalRequests: 0,
    activeRequests: 0,
    completedRequests: 0,
    averageBidsPerRequest: 0,
    averageResponseTime: 0,
    regionBreakdown: {},
    ageBreakdown: {},
    coverageTypeBreakdown: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');

  useEffect(() => {
    fetchStats();
  }, [timeRange]);

  async function fetchStats() {
    try {
      setLoading(true);
      setError(null);

      // Get date range
      const now = new Date();
      const startDate = timeRange === 'all' ? null :
        new Date(now.setDate(now.getDate() - parseInt(timeRange)));

      // Fetch requests with their bids
      const { data: requests, error: requestsError } = await supabase
        .from('insurance_requests')
        .select(`
          *,
          bids (
            id,
            amount,
            status,
            created_at
          )
        `)
        .gte('created_at', startDate ? startDate.toISOString() : null);

      if (requestsError) throw requestsError;

      // Calculate stats
      const stats = {
        totalRequests: requests?.length || 0,
        activeRequests: requests?.filter(r => r.status === 'bidding').length || 0,
        completedRequests: requests?.filter(r => r.status === 'bidding_complete').length || 0,
        averageBidsPerRequest: 0,
        averageResponseTime: 0,
        regionBreakdown: {},
        ageBreakdown: {},
        coverageTypeBreakdown: {}
      };

      // Calculate averages and breakdowns
      if (requests) {
        // Average bids per request
        const totalBids = requests.reduce((sum, req) => sum + (req.bids?.length || 0), 0);
        stats.averageBidsPerRequest = totalBids / requests.length || 0;

        // Average response time
        const responseTimes = requests
          .filter(req => req.bids && req.bids.length > 0)
          .map(req => {
            const firstBidTime = new Date(req.bids[0].created_at).getTime();
            const requestTime = new Date(req.created_at).getTime();
            return (firstBidTime - requestTime) / (1000 * 60 * 60); // Convert to hours
          });
        stats.averageResponseTime = responseTimes.length > 0
          ? responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length
          : 0;

        // Region breakdown
        stats.regionBreakdown = requests.reduce((acc, req) => {
          acc[req.region] = (acc[req.region] || 0) + 1;
          return acc;
        }, {});

        // Age breakdown
        stats.ageBreakdown = requests.reduce((acc, req) => {
          const ageRange = 
            req.age < 25 ? '18-24' :
            req.age < 35 ? '25-34' :
            req.age < 45 ? '35-44' :
            req.age < 55 ? '45-54' :
            req.age < 65 ? '55-64' : '65+';
          acc[ageRange] = (acc[ageRange] || 0) + 1;
          return acc;
        }, {});

        // Coverage type breakdown
        stats.coverageTypeBreakdown = requests.reduce((acc, req) => {
          acc[req.coverage_type] = (acc[req.coverage_type] || 0) + 1;
          return acc;
        }, {});
      }

      setStats(stats);
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError('Failed to load request statistics');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
          <BarChart className="w-6 h-6 text-indigo-600 mr-2" />
          Request Statistics
        </h2>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value as any)}
          className="border rounded-md px-3 py-2"
        >
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
          <option value="all">All Time</option>
        </select>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-indigo-100 rounded">
              <Shield className="h-6 w-6 text-indigo-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">Total Requests</h3>
              <p className="text-2xl font-semibold text-gray-900">{stats.totalRequests}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">Active Requests</h3>
              <p className="text-2xl font-semibold text-gray-900">{stats.activeRequests}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">Avg Bids/Request</h3>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.averageBidsPerRequest.toFixed(1)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">Avg Response Time</h3>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.averageResponseTime.toFixed(1)}h
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Region Breakdown */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Region Breakdown</h3>
          <div className="space-y-4">
            {Object.entries(stats.regionBreakdown).map(([region, count]) => (
              <div key={region} className="flex items-center">
                <span className="text-sm text-gray-500 w-24 sm:w-32 capitalize">
                  {region.replace('_', ' ')}
                </span>
                <div className="flex-1 ml-4">
                  <div className="relative h-4 bg-gray-100 rounded">
                    <div
                      className="absolute left-0 top-0 h-full bg-indigo-600 rounded"
                      style={{
                        width: `${(count / stats.totalRequests) * 100}%`
                      }}
                    />
                  </div>
                </div>
                <span className="ml-4 text-sm text-gray-600">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Age Breakdown */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Age Distribution</h3>
          <div className="space-y-4">
            {Object.entries(stats.ageBreakdown).map(([range, count]) => (
              <div key={range} className="flex items-center">
                <span className="text-sm text-gray-500 w-20">{range}</span>
                <div className="flex-1 ml-4">
                  <div className="relative h-4 bg-gray-100 rounded">
                    <div
                      className="absolute left-0 top-0 h-full bg-green-600 rounded"
                      style={{
                        width: `${(count / stats.totalRequests) * 100}%`
                      }}
                    />
                  </div>
                </div>
                <span className="ml-4 text-sm text-gray-600">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Coverage Type Breakdown */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Coverage Types</h3>
          <div className="space-y-4">
            {Object.entries(stats.coverageTypeBreakdown).map(([type, count]) => (
              <div key={type} className="flex items-center">
                <span className="text-sm text-gray-500 w-32 capitalize">
                  {type.replace(/-/g, ' ')}
                </span>
                <div className="flex-1 ml-4">
                  <div className="relative h-4 bg-gray-100 rounded">
                    <div
                      className="absolute left-0 top-0 h-full bg-blue-600 rounded"
                      style={{
                        width: `${(count / stats.totalRequests) * 100}%`
                      }}
                    />
                  </div>
                </div>
                <span className="ml-4 text-sm text-gray-600">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
